import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Divider, Grid, Segment } from "semantic-ui-react";
import { RootState } from "../../../../app/store/configureStore";
import { Student } from "../../../students/types";
import { downloadFile } from "../../../../app/helpers/fileHelper";
import agent from "../../../../app/api/agent";
import StudentProfileAgreementGuide from "./StudentProfileAgreementGuide";
import "./styles.less";
import StudentProfileAgreementPending from "./StudentProfileAgreementPending";

interface Props {
  student: Student;
}

const StudentProfileAgreement = ({ student }: Props) => {
  const { t } = useTranslation();
  const agreementState = useSelector((state: RootState) => state.currentUser.agreement);

  const download = async (fileId: string) => {
    const response = await agent.Files.getAgreement(fileId);
    downloadFile(response);
  };

  const renderContent = () => {
    if (agreementState?.status === "approved") {
      return (
        <>
          <p>{t("my_profile.student.agreement.approved_text")}</p>
          {agreementState.file && (
            <button className="links__item" onClick={() => download(agreementState.file!)}>
              {t("my_profile.student.agreement.file")}
              <span>{t("common.download")}</span>
            </button>
          )}
        </>
      );
    } else if (agreementState?.status === "uploaded") {
      return (
        <>
          <p>{t("my_profile.student.agreement.uploaded_text")}</p>
          {agreementState.file && (
            <button className="links__item" onClick={() => download(agreementState.file!)}>
              {t("my_profile.student.agreement.file")}
              <span>{t("common.download")}</span>
            </button>
          )}
        </>
      );
    } else if (agreementState?.status === "virtualsignpending") {
      return <StudentProfileAgreementPending student={student} />;
    } else if (agreementState?.status === "virtualsigncompleted") {
      return (
        <>
          <p>{t("my_profile.student.agreement.virtual_sign_completed_text")}</p>
          <Grid className="profile-data-table" stackable columns="equal">
            <Grid.Row>
              <Grid.Column>
                <div className="data-row-wrapper">
                  <div>
                    <h4>{t("my_profile.student.agreement.signer_fullname")}</h4>
                  </div>
                  <div>{agreementState.virtualSignSignerName}</div>
                </div>
                <Divider className="profile-data-table-divider" />
                <div className="data-row-wrapper">
                  <div>
                    <h4>{t("my_profile.student.agreement.signer_phone")}</h4>
                  </div>
                  <div>{agreementState.virtualSignSignerPhone}</div>
                </div>
                <Divider className="profile-data-table-divider" />
                <div className="data-row-wrapper">
                  <div>
                    <span>
                      <h4>{t("my_profile.student.agreement.allow_photos")}</h4>
                    </span>
                  </div>
                  <div>{t(agreementState.allowPhotos ? "common.yes" : "common.no")}</div>
                </div>
                <Divider className="profile-data-table-divider" />
                <div className="data-row-wrapper">
                  <div>
                    <h4>{t("my_profile.student.agreement.allow_travel")}</h4>
                  </div>
                  <div>{t(agreementState.allowTravel ? "common.yes" : "common.no")}</div>
                </div>
                <Divider className="profile-data-table-divider" />
                <div className="data-row-wrapper">
                  <div>
                    <h4>{t("my_profile.student.agreement.participant_is_adult")}</h4>
                  </div>
                  <div>{t(agreementState.isOver18 ? "common.yes" : "common.no")}</div>
                </div>
                <Divider className="profile-data-table-divider" />
                <div className="data-row-wrapper">
                  <div>
                    <h4>{t("my_profile.student.agreement.valid_to")}</h4>
                  </div>
                  <div>
                    {agreementState.validTo
                      ? new Date(agreementState.validTo)
                          .toLocaleDateString("lt-LT", { year: "numeric", month: "2-digit", day: "2-digit" })
                          .replace(/\//g, ".")
                      : ""}
                  </div>
                </div>
                <Divider className="profile-data-table-divider" />
                {agreementState.file && (
                  <button
                    className="links__item"
                    onClick={() => download(agreementState.file!)}
                    style={{ marginTop: "1em" }}
                  >
                    {t("my_profile.student.agreement.file")}
                    <span>{t("common.download")}</span>
                  </button>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      );
    } else {
      return <StudentProfileAgreementGuide student={student} />;
    }
  };

  return (
    <Segment padded="very">
      <h3>{t("my_profile.student.agreement.title")}</h3>
      {renderContent()}
    </Segment>
  );
};

export default StudentProfileAgreement;
